import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/firebaseconfig';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import ProgressBar from '../Home/progessbar';
import { Link } from 'react-router-dom';
import { MapPin, Building2, ArrowRight, Search, CheckCircle, Clock, Zap } from 'lucide-react';

const ProjectSection = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- FILTER STATES ---
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const q = query(collection(db, "projects"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setProjects(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // --- DYNAMIC STATS ---
  const totalCompleted = projects.filter(p => Number(p.percentage) === 100).length;
  const totalOngoing = projects.length - totalCompleted;

  const availableStates = useMemo(() => [...new Set(projects.map(p => p.state).filter(Boolean))].sort(), [projects]);
  const availableCities = useMemo(() => {
    const filtered = selectedState ? projects.filter(p => p.state === selectedState) : projects;
    return [...new Set(filtered.map(p => p.city).filter(Boolean))].sort();
  }, [selectedState, projects]);

  const filteredProjects = projects.filter(project => {
    const matchState = !selectedState || project.state === selectedState;
    const matchCity = !selectedCity || project.city === selectedCity;
    const isDone = Number(project.percentage) === 100;
    const matchStatus = statusFilter === "all" ? true : (statusFilter === "completed" ? isDone : !isDone);
    const search = searchTerm.toLowerCase().trim();
    return matchState && matchCity && matchStatus && (
      project.title?.toLowerCase().includes(search) || project.client?.toLowerCase().includes(search)
    );
  });

  if (loading) return <div className="py-20 text-center text-success">Loading Denial Portfolio...</div>;

  return (
    <section className="py-5 bg-light">
      <div className="container">

        {/* --- MINIMALIST HEADER WITH INTEGRATED STATS --- */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end mb-4 border-bottom pb-3">
          <div>
            <h2 className="fw-bold mb-1 text-dark tracking-tight">Project <span className="text-success">Portfolio</span></h2>
            <div className="d-flex align-items-center gap-3 mt-1">
              <span className="small text-muted d-flex align-items-center">
                <CheckCircle size={14} className="text-success me-1" /> <strong>{totalCompleted}</strong>&nbsp;Completed projects
              </span>
              <span className="text-light-emphasis">|</span>
              <span className="small text-muted d-flex align-items-center">
                <Clock size={14} className="text-warning me-1" /> <strong>{totalOngoing}</strong>&nbsp;On-Going Sites
              </span>
              <span className="text-light-emphasis">|</span>
              <span className="small text-muted">Total Projects: <strong>{projects.length}</strong></span>
            </div>
          </div>

          {/* Search Integrated into Header Row */}
          <div className="mt-3 mt-md-0" style={{ width: '300px' }}>
            <div className="input-group input-group-sm bg-white rounded-pill px-2 border shadow-sm">
              <span className="input-group-text bg-transparent border-0"><Search size={14} className="text-muted" /></span>
              <input
                type="text"
                className="form-control bg-transparent border-0 shadow-none"
                placeholder="Search by client or project..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* --- COMPACT FILTER CONTROL BAR --- */}
        {/* --- IMPROVED FILTER CONTROL BAR --- */}
        <div className="row g-2 mb-4 align-items-center">
          <div className="col-auto">
            <select
              className="form-select form-select-sm border-0 shadow-sm px-3 py-3 bg-white"
              style={{ width: '160px' }} // Set a fixed width to prevent text pushing
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Projects</option>
              <option value="completed">Completed</option>
              <option value="ongoing">On-Going</option>
            </select>
          </div>

          <div className="col-auto">
            <select
              className="form-select form-select-sm border-0 shadow-sm px-3 py-3 bg-white"
              style={{ width: '160px' }}
              value={selectedState}
              onChange={(e) => { setSelectedState(e.target.value); setSelectedCity(""); }}
            >
              <option value="">Select State</option>
              {availableStates.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="col-auto">
            <select
              className="form-select form-select-sm  border-0 shadow-sm px-3 py-3 bg-white"
              style={{ width: '160px' }}
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              disabled={!selectedState}
            >
              <option value="">Select City</option>
              {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="col-auto ms-auto">
            <button
              className="btn btn-sm text-muted fw-bold p-0 border-0"
              onClick={() => { setSelectedState(""); setSelectedCity(""); setStatusFilter("all"); setSearchTerm(""); }}
            >
              RESET FILTERS
            </button>
          </div>
        </div>

        {/* --- COMPACT GRID --- */}
        <div className="row g-3">
          {filteredProjects.map((project) => {
            const isDone = Number(project.percentage) === 100;
            return (
              <div className="col-xl-3 col-lg-4 col-md-6" key={project.id}>
                <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden hover-lift bg-white">
                  <div className="position-relative" style={{ height: '140px' }}>
                    <img src={project.imageUrl} className="w-100 h-100 object-fit-cover" alt={project.title} />
                    <div className="position-absolute top-0 end-0 m-2">
                      <span className={`badge ${isDone ? 'bg-success' : 'bg-warning text-dark'} rounded-pill extra-small shadow-sm`}>
                        {isDone ? 'Completed' : 'On-Going'}
                      </span>
                    </div>
                  </div>

                  <div className="card-body p-3 d-flex flex-column">
                    <span className="text-success fw-bold extra-small text-uppercase" style={{ fontSize: '10px' }}>{project.category || 'HT/LT Work'}</span>
                    <h6 className="fw-bold text-dark mb-1 line-clamp-1 mt-1">{project.title}</h6>
                    <p className="text-muted mb-2 d-flex align-items-center" style={{ fontSize: '11px' }}>
                      <Building2 size={12} className="me-1" /> {project.client}
                    </p>

                    <p className="text-secondary mb-3 line-clamp-2" style={{ fontSize: '12px', lineHeight: '1.4' }}>{project.desc}</p>

                    <div className="mt-auto">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-muted" style={{ fontSize: '10px' }}><MapPin size={10} className="me-1" />{project.city}</span>
                        <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>{project.percentage}%</span>
                      </div>
                      <div className="progress mb-3" style={{ height: '4px' }}>
                        <div className={`progress-bar rounded-pill ${isDone ? 'bg-success' : 'bg-warning'}`} style={{ width: `${project.percentage}%` }}></div>
                      </div>
                      <Link to={`/project/${project.id}`} className="btn btn-sm btn-success w-100 rounded-pill fw-bold extra-small py-2 border-0 shadow-sm">
                        VIEW DETAILS <ArrowRight size={12} className="ms-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .hover-lift { transition: transform 0.25s ease; }
        .hover-lift:hover { transform: translateY(-5px); box-shadow: 0 8px 20px rgba(0,0,0,0.1) !important; }
        .extra-small { font-size: 0.7rem; }
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </section>
  );
};

export default ProjectSection;